/** The slice of Cloudflare's widget api this uses. */
export type TurnstileApi = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => string | undefined;
  remove: (widgetId: string) => void;
  /** Throws the solved token away and asks the visitor again */
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// Explicit rather than implicit rendering: the widget belongs to a form that
// may mount and unmount several times on one page
const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loading: Promise<TurnstileApi | null> | null = null;

/**
 * The page's CSP nonce, read from a script the server nonced. Browsers hide the
 * attribute but keep the property, so the attribute selector still matches.
 */
export function pageNonce(): string | undefined {
  const nonce =
    document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
  return nonce ? nonce : undefined;
}

/**
 * Fetch Cloudflare's widget script, once per page.
 *
 * Resolves `null` when there is no browser to load it into, or when the script
 * cannot be reached — offline, or refused by a content blocker. The form then
 * says so instead of waiting on a widget that will never arrive, since sending
 * it without a token would only be refused.
 */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(null);
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (loading) {
    return loading;
  }

  loading = new Promise<TurnstileApi | null>((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    // Under 'strict-dynamic' only a nonce is sure to let the script in
    const nonce = pageNonce();
    if (nonce) script.nonce = nonce;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(window.turnstile ?? null));
    script.addEventListener('error', () => {
      // Cleared so a later form tries again rather than inheriting this failure
      loading = null;
      resolve(null);
    });
    document.head.appendChild(script);
  });

  return loading;
}
